import React, { useState, useEffect } from 'react';
import { FileText, Plus, Edit, Trash2, Loader2, DollarSign, CheckCircle, Send, X, Download, Receipt, Mail } from 'lucide-react';
import { quoteAPI, customerAPI, invoiceAPI, quotePdfAPI } from '../api/client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const STATUSES = [
  { id: 'all', label: 'All' },
  { id: 'draft', label: 'Draft', class: 'bg-gray-500/20 text-gray-300' },
  { id: 'sent', label: 'Sent', class: 'bg-blue-500/20 text-blue-500' },
  { id: 'approved', label: 'Approved', class: 'bg-green-500/20 text-green-500' },
  { id: 'declined', label: 'Declined', class: 'bg-red-500/20 text-red-500' },
];

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: '',
    title: '',
    description: '',
    line_items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }],
  });

  useEffect(() => { loadData(); }, []);
  useEffect(() => {
    if (statusFilter === 'all') setFiltered(quotes);
    else setFiltered(quotes.filter((q) => q.status === statusFilter));
  }, [quotes, statusFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [q, c] = await Promise.all([quoteAPI.list(), customerAPI.list()]);
      setQuotes(q || []);
      setCustomers(c || []);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const updateLineItem = (index, field, value) => {
    const items = [...formData.line_items];
    items[index][field] = value;
    if (field === 'quantity' || field === 'unit_price') {
      items[index].total = (parseFloat(items[index].quantity) || 0) * (parseFloat(items[index].unit_price) || 0);
    }
    setFormData({ ...formData, line_items: items });
  };

  const addLineItem = () => setFormData({ ...formData, line_items: [...formData.line_items, { description: '', quantity: 1, unit_price: 0, total: 0 }] });
  const removeLineItem = (i) => setFormData({ ...formData, line_items: formData.line_items.filter((_, idx) => idx !== i) });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id || !formData.title) {
      toast.error('Customer and title required');
      return;
    }
    setSubmitting(true);
    try {
      await quoteAPI.create(formData);
      toast.success('Quote created!');
      setDialogOpen(false);
      setFormData({ customer_id: '', title: '', description: '', line_items: [{ description: '', quantity: 1, unit_price: 0, total: 0 }] });
      loadData();
    } catch (e) { toast.error('Failed to create quote'); }
    finally { setSubmitting(false); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await quoteAPI.update(id, { status });
      toast.success(`Quote ${status}`);
      loadData();
    } catch (e) { toast.error('Failed to update'); }
  };

  const downloadPdf = async (quote) => {
    try {
      const blob = await quotePdfAPI.downloadPdf(quote.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${quote.quote_number || quote.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not download PDF'); }
  };

  const convertToInvoice = async (quote) => {
    try {
      const inv = await invoiceAPI.convertFromQuote(quote.id);
      toast.success(`Invoice ${inv.invoice_number} created`);
      loadData();
    } catch (e) { toast.error('Could not convert quote'); }
  };

  const emailQuote = async (quote) => {
    try {
      const result = await quotePdfAPI.email(quote.id);
      toast.success(`Email sent to ${result.to}`);
      loadData();
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || 'Failed to send email';
      if (status === 503) {
        toast.error('Email is not configured. Add RESEND_API_KEY to backend .env to enable sending.');
      } else {
        toast.error(msg);
      }
    }
  };

  const formatCurrency = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);
  const getCustomerName = (id) => {
    const c = customers.find((c) => c.id === id);
    return c ? `${c.first_name} ${c.last_name}` : 'Unknown';
  };

  const totalApproved = quotes.filter((q) => q.status === 'approved').reduce((s, q) => s + (q.total || 0), 0);
  const totalPending = quotes.filter((q) => q.status === 'sent').reduce((s, q) => s + (q.total || 0), 0);
  const subtotal = formData.line_items.reduce((s, i) => s + (i.total || 0), 0);

  return (
    <div className="space-y-6 fade-in" data-testid="quotes-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2">Quotes & Estimates</h1>
          <p className="text-muted-foreground">Create professional quotes for your customers</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="btn-gradient-primary" data-testid="add-quote-button">
          <Plus className="w-4 h-4 mr-2" /> New Quote
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-blue border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total Quotes</p>
            <p className="text-2xl font-bold">{quotes.length}</p>
          </CardContent>
        </Card>
        <Card className="stat-orange border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Pending</p>
            <p className="text-2xl font-bold text-orange-500">{formatCurrency(totalPending)}</p>
          </CardContent>
        </Card>
        <Card className="stat-green border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Approved</p>
            <p className="text-2xl font-bold text-green-500">{formatCurrency(totalApproved)}</p>
          </CardContent>
        </Card>
        <Card className="stat-purple border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Approval Rate</p>
            <p className="text-2xl font-bold">{quotes.length > 0 ? ((quotes.filter(q=>q.status==='approved').length / quotes.length) * 100).toFixed(0) : 0}%</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <TabsList className="w-full justify-start overflow-x-auto no-scrollbar">
          {STATUSES.map((s) => <TabsTrigger key={s.id} value={s.id}>{s.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {loading ? (
        <Card><CardContent className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></CardContent></Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No quotes yet</h3>
            <Button onClick={() => setDialogOpen(true)} className="btn-gradient-primary">
              <Plus className="w-4 h-4 mr-2" /> Create First Quote
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((q) => {
            const sc = STATUSES.find((s) => s.id === q.status);
            return (
              <Card key={q.id} className="card-hover">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{q.title}</h3>
                        <Badge className={`${sc?.class} border-0`}>{q.status}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{q.quote_number} • {getCustomerName(q.customer_id)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-500">{formatCurrency(q.total)}</p>
                      <div className="flex gap-2 mt-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadPdf(q)}
                          data-testid={`quote-${q.id}-pdf-button`}
                        >
                          <Download className="w-3 h-3 mr-1" />PDF
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => emailQuote(q)}
                          data-testid={`quote-${q.id}-email-button`}
                        >
                          <Mail className="w-3 h-3 mr-1" />Email
                        </Button>
                        {q.status !== 'converted' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => convertToInvoice(q)}
                            data-testid={`quote-${q.id}-convert-button`}
                          >
                            <Receipt className="w-3 h-3 mr-1" />Invoice
                          </Button>
                        )}
                        {q.status === 'draft' && (
                          <Button size="sm" onClick={() => handleStatusChange(q.id, 'sent')} className="btn-gradient-info">
                            <Send className="w-3 h-3 mr-1" />Send
                          </Button>
                        )}
                        {q.status === 'sent' && (
                          <>
                            <Button size="sm" onClick={() => handleStatusChange(q.id, 'approved')} className="btn-gradient-success">
                              <CheckCircle className="w-3 h-3 mr-1" />Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleStatusChange(q.id, 'declined')}>
                              <X className="w-3 h-3 mr-1" />Decline
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Quote</DialogTitle>
            <DialogDescription>Generate a professional quote for your customer</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Customer *</Label>
                <Select value={formData.customer_id} onValueChange={(v) => setFormData({ ...formData, customer_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Quote title" required />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Line Items</Label>
                <Button type="button" size="sm" variant="outline" onClick={addLineItem}>
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {formData.line_items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-6" placeholder="Description" value={item.description} onChange={(e) => updateLineItem(idx, 'description', e.target.value)} />
                    <Input className="col-span-2" type="number" min="0" placeholder="Qty" value={item.quantity} onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)} />
                    <Input className="col-span-2" type="number" step="0.01" min="0" placeholder="Price" value={item.unit_price} onChange={(e) => updateLineItem(idx, 'unit_price', e.target.value)} />
                    <div className="col-span-1 text-sm font-semibold">{formatCurrency(item.total)}</div>
                    <Button type="button" size="sm" variant="outline" className="col-span-1" onClick={() => removeLineItem(idx)}>
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-muted rounded-lg flex items-center justify-between">
              <span className="text-lg font-medium">Total:</span>
              <span className="text-3xl font-bold text-green-500">{formatCurrency(subtotal)}</span>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-gradient-primary" disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Quote'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
